import { useEffect, useState } from 'react'
import { desktop } from '@/lib/printTransport'

export interface PendingUpdate {
  version: string
}

/**
 * The staged desktop update, or null when there is none / we're in a browser
 * (F-FE-N47).
 *
 * Pulls AND subscribes, deliberately. electron-updater checks for updates at
 * launch, so `update-downloaded` can fire before React has mounted — a listener
 * on its own would silently miss it and the banner would not appear until the
 * next 30-minute check. Conversely a one-shot read would miss an update that
 * arrives mid-shift. Both paths are needed; whichever resolves first wins and
 * the other is idempotent.
 */
export function useDesktopUpdate(): PendingUpdate | null {
  const [pending, setPending] = useState<PendingUpdate | null>(null)

  useEffect(() => {
    const bridge = desktop()
    if (!bridge) return

    let cancelled = false

    void bridge
      .getUpdateState?.()
      .then((s) => {
        if (!cancelled) setPending(s)
      })
      .catch(() => {
        /* shell too old to answer — leave the banner hidden */
      })

    const unsubscribe = bridge.onUpdateState?.((s) => {
      if (!cancelled) setPending(s)
    })

    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [])

  return pending
}
